import { BehaviorSubject } from 'rxjs';
import {
  createMcpAccessProfile as createMcpAccessProfileApi,
  listMcpAccessProfiles,
  revokeMcpAccessProfile as revokeMcpAccessProfileApi,
  rotateMcpAccessProfile as rotateMcpAccessProfileApi,
  updateMcpAccessProfile as updateMcpAccessProfileApi,
  McpAccessProfile,
  McpAccessProfileInput,
  McpAccessProfileUpdate,
  McpAccessProfileWithToken
} from '../Api/McpAccess/McpAccessApi';
import { addError } from './Error';

export type {
  McpAccessScope,
  McpAccessProfile,
  McpAccessProfileWithToken,
  McpAccessProfileInput,
  McpAccessProfileUpdate
} from '../Api/McpAccess/McpAccessApi';

const ERROR_KEY = 'mcp-access';

const mcpAccessProfilesSubject = new BehaviorSubject<McpAccessProfile[]>([]);
const mcpAccessLoadingSubject = new BehaviorSubject<boolean>(false);
const mcpAccessListErrorSubject = new BehaviorSubject<string | null>(null);

let latestListToken = 0;

export const getMcpAccessProfilesSubject = (): BehaviorSubject<McpAccessProfile[]> => mcpAccessProfilesSubject;
export const getMcpAccessLoadingSubject = (): BehaviorSubject<boolean> => mcpAccessLoadingSubject;
export const getMcpAccessListErrorSubject = (): BehaviorSubject<string | null> => mcpAccessListErrorSubject;

const clearError = () => addError(ERROR_KEY, undefined);

export const fetchMcpAccessProfiles = async () => {
  const token = ++latestListToken;
  mcpAccessLoadingSubject.next(true);
  mcpAccessListErrorSubject.next(null);

  const { data, error } = await listMcpAccessProfiles();

  // A newer fetch superseded this one; drop the stale response so it cannot overwrite fresher data.
  if (token !== latestListToken) return;

  if (data) {
    clearError();
    mcpAccessProfilesSubject.next(data);
  }
  if (error) {
    addError(ERROR_KEY, error.message);
    mcpAccessListErrorSubject.next(error.message);
  }

  mcpAccessLoadingSubject.next(false);
};

export const createMcpAccessProfile = async (
  input: McpAccessProfileInput
): Promise<McpAccessProfileWithToken | null> => {
  const { data, error } = await createMcpAccessProfileApi(input);

  if (data) {
    clearError();
    // Refresh in the background so the one-time token reveals immediately; latestListToken keeps ordering safe.
    void fetchMcpAccessProfiles();
    return data;
  }
  if (error) addError(ERROR_KEY, error.message);
  return null;
};

export const updateMcpAccessProfile = async (
  id: string,
  update: McpAccessProfileUpdate
): Promise<McpAccessProfile | null> => {
  const { data, error } = await updateMcpAccessProfileApi(id, update);

  if (data) {
    clearError();
    void fetchMcpAccessProfiles();
    return data;
  }
  if (error) addError(ERROR_KEY, error.message);
  return null;
};

export const rotateMcpAccessProfile = async (id: string): Promise<McpAccessProfileWithToken | null> => {
  const { data, error } = await rotateMcpAccessProfileApi(id);

  if (data) {
    clearError();
    void fetchMcpAccessProfiles();
    return data;
  }
  if (error) addError(ERROR_KEY, error.message);
  return null;
};

export const revokeMcpAccessProfile = async (id: string): Promise<boolean> => {
  const { error } = await revokeMcpAccessProfileApi(id);

  if (error) {
    addError(ERROR_KEY, error.message);
    return false;
  }
  clearError();
  void fetchMcpAccessProfiles();
  return true;
};
